import React, { useState } from "react";
import { faPencil, faTrash, faArrowRight, faCalendar, faRepeat, faLayerGroup } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ActionMenu from "../action-menu/ActionMenu";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import { ConfirmationDialog } from "../confirmation-dialog/ConfirmationDialog";
import ListSelectionDialog from "../list-selection-dialog/ListSelectionDialog";
import { Habit, Collection, HabitStep } from "../../types";
import { useTranslation } from "react-i18next";
import { HabitHistoryModal } from "../habit-history-modal/HabitHistoryModal";
import { AddHabitModal } from "../add-habit-modal/AddHabitModal";
import {
    formatDateToLocal,
    getHabitCurrentStreak,
    getHabitProgressForDate,
    getHabitProgressRatioForDate,
    getHabitPeriodProgress,
    getHabitPeriodTargetDays,
    getHabitTrackedDays,
    getHabitTargetCount,
    isHabitDateCompleted,
    isHabitPeriodCompleted,
    isPeriodHabit,
    isHabitDateTracked,
    advanceHabitProgressDate,
    setHabitProgressDate,
} from "../../utils/habitUtils";

import "./HabitElement.css";

interface HabitElementProps {
    element: Habit;
    deleteElement: (id: string) => void;
    updateElement: (updatedElement: Habit) => void;
    lists: Collection[];
    selectedListId: string;
    moveElementToList: (element: Habit, listId: string) => void;
    isHistoryOpen?: boolean;
    onOpenHistory?: (id: string) => void;
    onCloseHistory?: () => void;
    historyMonth?: Date;
    onHistoryMonthChange?: (month: Date) => void;
    onMoveToSection?: (element: Habit) => void;
}

export const HabitElement: React.FC<HabitElementProps> = ({
    element,
    deleteElement,
    updateElement,
    lists,
    selectedListId,
    moveElementToList,
    isHistoryOpen,
    onOpenHistory,
    onCloseHistory,
    historyMonth,
    onHistoryMonthChange,
    onMoveToSection,
}) => {
    const { t } = useTranslation();
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showListSelectionDialog, setShowListSelectionDialog] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [quickLogDate, setQuickLogDate] = useState<string | undefined>(undefined);
    const [quickLogValue, setQuickLogValue] = useState('');
    const historyIsOpen = isHistoryOpen ?? showHistoryModal;

    const handleOpenHistory = () => {
        if (onOpenHistory) {
            onOpenHistory(element.id);
            return;
        }

        setShowHistoryModal(true);
    };

    const handleCloseHistory = () => {
        if (onCloseHistory) {
            onCloseHistory();
            return;
        }

        setShowHistoryModal(false);
    };

    const handleToggleComplete = (dateStr: string) => {
        const progressUpdate = advanceHabitProgressDate(element, dateStr);

        updateElement({
            ...element,
            ...progressUpdate,
        });
    };

    const handleProgressClick = (dateStr: string) => {
        if (getHabitTargetCount(element) > 1) {
            setQuickLogDate(dateStr);
            setQuickLogValue(String(getHabitProgressForDate(element, dateStr) || ''));
            return;
        }

        handleToggleComplete(dateStr);
    };

    const handleSaveQuickLog = () => {
        if (!quickLogDate) return;

        const progressUpdate = setHabitProgressDate(element, quickLogDate, Number(quickLogValue));
        updateElement({
            ...element,
            ...progressUpdate,
        });
        setQuickLogDate(undefined);
        setQuickLogValue('');
    };

    const handleEdit = () => {
        setShowEditDialog(true);
    };

    const handleDelete = () => {
        deleteElement(element.id);
        setShowDeleteConfirmationDialog(false);
    };

    const actions = [
        {
            label: t('habitElement.history'),
            icon: faCalendar,
            callback: handleOpenHistory,
        },
        {
            label: t('habitElement.editHabit'),
            icon: faPencil,
            callback: handleEdit,
        },
        {
            label: t('habitElement.moveToCollection'),
            icon: faArrowRight,
            callback: () => setShowListSelectionDialog(true),
        },
        ...(onMoveToSection ? [{
            label: t('sections.moveToSection'),
            icon: faLayerGroup,
            callback: () => onMoveToSection(element),
        }] : []),
        {
            label: t('habitElement.deleteHabit'),
            icon: faTrash,
            callback: () => setShowDeleteConfirmationDialog(true),
        },
    ];

    const getProgressDays = () => {
        const days = [];
        const dayLabels = [
            t('habitElement.monday'),
            t('habitElement.tuesday'),
            t('habitElement.wednesday'),
            t('habitElement.thursday'),
            t('habitElement.friday'),
            t('habitElement.saturday'),
            t('habitElement.sunday')
        ];
        const today = new Date();
        const currentDay = today.getDay();
        const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
        const todayStr = formatDateToLocal(today);

        const monday = new Date(today);
        monday.setDate(today.getDate() + mondayOffset);

        for (let i = 0; i < 7; i++) {
            const date = new Date(monday);
            date.setDate(monday.getDate() + i);
            const dateStr = formatDateToLocal(date);
            const isToday = dateStr === todayStr;
            const isFuture = date > today;
            const isTracked = isHabitDateTracked(element, dateStr);
            const isCompleted = !isFuture && isHabitDateCompleted(element, dateStr);
            const progress = !isFuture ? getHabitProgressForDate(element, dateStr) : 0;
            const progressRatio = !isFuture ? getHabitProgressRatioForDate(element, dateStr) : 0;
            const isPeriodComplete = isPeriodHabit(element) && isHabitPeriodCompleted(element, dateStr);
            days.push({ date: dateStr, isCompleted, isPeriodComplete, label: dayLabels[i], isToday, isFuture, isTracked, progress, progressRatio });
        }

        return days;
    };

    const getTrackedDaysLabel = () => {
        if (element.frequency === 'monthly' && isPeriodHabit(element)) {
            return t('habitElement.daysPerMonth', { count: getHabitPeriodTargetDays(element, formatDateToLocal(new Date())) });
        }

        if (element.frequency === 'monthly') {
            return t('habitElement.monthlyOnDay', { day: element.monthlyDay || 1 });
        }

        if (element.frequency === 'weekly' && isPeriodHabit(element)) {
            return t('habitElement.daysPerWeek', { count: getHabitPeriodTargetDays(element, formatDateToLocal(new Date())) });
        }

        if (element.frequency === 'daily' && getHabitTrackedDays(element).length === 7) {
            return t('habitElement.everyDay');
        }

        const trackedDays = getHabitTrackedDays(element);
        const dayLabels = [
            t('habitElement.monday'),
            t('habitElement.tuesday'),
            t('habitElement.wednesday'),
            t('habitElement.thursday'),
            t('habitElement.friday'),
            t('habitElement.saturday'),
            t('habitElement.sunday')
        ].map((dayLabel) => dayLabel.slice(0, 2).toUpperCase());

        if (trackedDays.length === 7) {
            return t('habitElement.everyDay');
        }

        return trackedDays.map((day) => dayLabels[day]).join(', ');
    };

    const getTargetLabel = () => {
        const targetParts = [element.count, element.unit?.trim()].filter(Boolean);
        return targetParts.join(' ');
    };

    const getSmartStepCompletionCount = () => {
        const stepStartedAt = element.smartHabit?.currentStepStartedAt || '0000-00-00';
        return (element.completedDates || []).filter((dateStr) => (
            dateStr >= stepStartedAt && isHabitDateCompleted(element, dateStr)
        )).length;
    };

    const getTrackedDaysSinceCurrentStepStarted = () => {
        const stepStartedAt = element.smartHabit?.currentStepStartedAt;
        if (!stepStartedAt) return 0;

        const [year, month, day] = stepStartedAt.split('-').map(Number);
        const cursor = new Date(year, month - 1, day);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let trackedDays = 0;
        while (cursor <= today) {
            if (isHabitDateTracked(element, formatDateToLocal(cursor))) {
                trackedDays++;
            }
            cursor.setDate(cursor.getDate() + 1);
        }

        return trackedDays;
    };

    const getSmartStepTitle = (step: string | HabitStep | undefined) => {
        return typeof step === 'string' ? step : step?.title || '';
    };

    const getSmartGoalTitle = () => {
        const smartHabit = element.smartHabit;
        if (!smartHabit?.enabled) return '';
        return smartHabit.goal?.title || smartHabit.longTermGoal || '';
    };

    const shouldShowSmartGoalTitle = () => {
        const goalTitle = getSmartGoalTitle().trim();
        return goalTitle && goalTitle !== element.title.trim();
    };

    const getNextSmartStep = () => {
        const smartHabit = element.smartHabit;
        if (!smartHabit?.enabled) return '';
        return getSmartStepTitle(smartHabit.steps[smartHabit.currentStepIndex + 1]);
    };

    const getProgressCountLabel = (progress: number) => {
        const targetCount = getHabitTargetCount(element);
        return targetCount > 1 ? `${progress}/${targetCount}` : String(progress);
    };

    const shouldShowSmartPrompt = () => {
        const smartHabit = element.smartHabit;
        if (!smartHabit?.enabled) return false;
        const promptThreshold = smartHabit.promptThreshold || 3;
        const completionCount = getSmartStepCompletionCount();
        const dismissedAtCount = smartHabit.nextStepPromptDismissedAtCompletionCount;

        if (
            typeof dismissedAtCount === 'number' &&
            completionCount < dismissedAtCount + promptThreshold
        ) {
            return false;
        }

        return completionCount >= promptThreshold;
    };

    const shouldShowEasierStepPrompt = () => {
        const smartHabit = element.smartHabit;
        if (!smartHabit?.enabled) return false;
        if (smartHabit.hardStepPromptDismissedAt === smartHabit.currentStepStartedAt) return false;

        return getTrackedDaysSinceCurrentStepStarted() >= (smartHabit.promptThreshold || 3)
            && getSmartStepCompletionCount() === 0;
    };

    const applySmartStep = (stepIndex: number) => {
        const smartHabit = element.smartHabit;
        if (!smartHabit?.enabled) return;

        const step = smartHabit.steps[stepIndex] || (stepIndex === smartHabit.steps.length ? smartHabit.goal : undefined);
        if (!step) return;

        const stepConfig = typeof step === 'string' ? undefined : step;

        updateElement({
            ...element,
            title: getSmartStepTitle(step) || element.title,
            note: stepConfig?.note,
            count: stepConfig?.count,
            unit: stepConfig?.unit,
            trackedDays: stepConfig?.trackedDays || element.trackedDays,
            frequency: stepConfig?.frequency || element.frequency,
            monthlyDay: undefined,
            periodTargetDays: stepConfig?.periodTargetDays || element.periodTargetDays,
            smartHabit: {
                ...smartHabit,
                currentStepIndex: stepIndex,
                currentStepStartedAt: formatDateToLocal(new Date()),
                hardStepPromptDismissedAt: undefined,
                nextStepPromptDismissedAt: undefined,
                nextStepPromptDismissedAtCompletionCount: undefined,
            },
        });
    };

    const handleGoToNextSmartStep = () => {
        const smartHabit = element.smartHabit;
        if (!smartHabit?.enabled) return;

        const nextStep = smartHabit.steps[smartHabit.currentStepIndex + 1];
        if (!nextStep) {
            if (smartHabit.goal) {
                const goalStepIndex = smartHabit.steps.length;
                applySmartStep(goalStepIndex);
                return;
            }

            return;
        }

        applySmartStep(smartHabit.currentStepIndex + 1);
    };

    const handleGoToPreviousSmartStep = () => {
        const smartHabit = element.smartHabit;
        if (!smartHabit?.enabled || smartHabit.currentStepIndex <= 0) return;
        applySmartStep(smartHabit.currentStepIndex - 1);
    };

    const handleKeepCurrentSmartStep = () => {
        const smartHabit = element.smartHabit;
        if (!smartHabit?.enabled) return;

        updateElement({
            ...element,
            smartHabit: {
                ...smartHabit,
                hardStepPromptDismissedAt: smartHabit.currentStepStartedAt,
            },
        });
    };

    const handleKeepCurrentStepFromNextPrompt = () => {
        const smartHabit = element.smartHabit;
        if (!smartHabit?.enabled) return;

        updateElement({
            ...element,
            smartHabit: {
                ...smartHabit,
                nextStepPromptDismissedAt: formatDateToLocal(new Date()),
                nextStepPromptDismissedAtCompletionCount: getSmartStepCompletionCount(),
            },
        });
    };

    const currentStreak = getHabitCurrentStreak(element);

    return (
        <div className="habit-element">
            <div className="habit-title-row">
                <div className="habit-heading">
                    <span className="element-kind-icon habit-kind-icon" aria-hidden="true">
                        <FontAwesomeIcon icon={faRepeat} />
                    </span>
                    <div className="habit-title-block">
                        <span className="element-type-label habit-type-label">{t('elementTypes.habit')}</span>
                        <div className="habit-title">{element.title}</div>
                    </div>
                </div>
                <div className="habit-title-actions">
                    <ActionMenu actions={actions} icon={faEllipsisV} buttonStyle={{}} buttonClassName="habit-menu-button" />
                </div>
            </div>
            {element.note?.trim() && (
                <div className="habit-note">{element.note}</div>
            )}
            {element.smartHabit?.enabled && (
                <div className="habit-smart-summary">
                    {shouldShowSmartGoalTitle() && (
                        <div className="habit-smart-goal">
                            <span>{t('habitElement.longTermGoal')}:</span> {getSmartGoalTitle()}
                        </div>
                    )}
                </div>
            )}
            <div className="habit-progress">
                {getProgressDays().map((day, index) => (
                    <div key={index} className="habit-progress-item">
                        <div
                            className={`habit-progress-circle ${day.isCompleted ? 'completed' : ''} ${day.isPeriodComplete && !day.isCompleted ? 'period-complete' : ''} ${day.progress > 0 && !day.isCompleted ? 'partial' : ''} ${day.isToday ? 'today' : ''} ${day.isFuture ? 'future' : ''} ${!day.isTracked ? 'untracked' : ''}`}
                            style={{ '--habit-progress': `${Math.round(day.progressRatio * 360)}deg` } as React.CSSProperties}
                            title={day.progress > 0 && getHabitTargetCount(element) > 1
                                ? `${day.label}: ${getProgressCountLabel(day.progress)}`
                                : day.label}
                            onClick={() => !day.isFuture && day.isTracked && handleProgressClick(day.date)}
                        >
                            <span className="habit-day-letter">{day.label.slice(0, 1)}</span>
                        </div>
                    </div>
                ))}
            </div>
            <div className="habit-meta-line">
                <span>{getTrackedDaysLabel()}</span>
                {isPeriodHabit(element) && (
                    <>
                        <span className="habit-meta-separator">{"\u00b7"}</span>
                        <span>{getHabitPeriodProgress(element, formatDateToLocal(new Date()))}/{getHabitPeriodTargetDays(element, formatDateToLocal(new Date()))}</span>
                    </>
                )}
                {getTargetLabel() && (
                    <>
                        <span className="habit-meta-separator">{"\u00b7"}</span>
                        <span title={t('habitElement.target')}>
                            {t('habitElement.target')}: <span>{getTargetLabel()}</span>
                        </span>
                    </>
                )}
                {currentStreak > 1 && (
                    <>
                        <span className="habit-meta-separator">{"\u00b7"}</span>
                        <span>{currentStreak} {t(isPeriodHabit(element) ? element.frequency === 'monthly' ? 'habitElement.monthStreak' : 'habitElement.weekStreak' : 'habitElement.dayStreak')}</span>
                    </>
                )}
            </div>
            {shouldShowSmartPrompt() && (
                <div className="habit-smart-prompt">
                    <div>
                        <strong>{t('habitElement.readyForNextStep')}</strong>
                        {getNextSmartStep() && <span>{getNextSmartStep()}</span>}
                    </div>
                    <div className="habit-smart-prompt-actions">
                        <button type="button" onClick={handleKeepCurrentStepFromNextPrompt}>
                            {t('habitElement.keepThisStep')}
                        </button>
                        <button type="button" onClick={handleGoToNextSmartStep}>
                            {getNextSmartStep() ? t('habitElement.goToNextStep') : t('habitElement.goToGoal')}
                        </button>
                    </div>
                </div>
            )}
            {shouldShowEasierStepPrompt() && (
                <div className="habit-smart-prompt habit-smart-prompt-warning">
                    <div>
                        <strong>{t('habitElement.stepMayBeTooHard')}</strong>
                        <span>{t('habitElement.tryEasierStep')}</span>
                    </div>
                    <div className="habit-smart-prompt-actions">
                        <button type="button" onClick={handleKeepCurrentSmartStep}>
                            {t('habitElement.keepThisStep')}
                        </button>
                        {element.smartHabit && element.smartHabit.currentStepIndex > 0 && (
                            <button type="button" onClick={handleGoToPreviousSmartStep}>
                                {t('habitElement.goToPreviousStep')}
                            </button>
                        )}
                        <button type="button" onClick={() => setShowEditDialog(true)}>
                            {t('habitElement.addEasierStep')}
                        </button>
                    </div>
                </div>
            )}
            {showDeleteConfirmationDialog && (
                <ConfirmationDialog
                    title={t('habitElement.deleteHabit')}
                    message={t('habitElement.deleteHabitConfirm')}
                    onConfirm={handleDelete}
                    onCancel={() => setShowDeleteConfirmationDialog(false)}
                    isDanger={true}
                />
            )}
            {showListSelectionDialog && (
                <ListSelectionDialog
                    lists={lists}
                    selectedListId={selectedListId}
                    onSelectList={(listId: string) => {
                        moveElementToList(element, listId);
                        setShowListSelectionDialog(false);
                    }}
                    onClose={() => setShowListSelectionDialog(false)}
                />
            )}
            {showEditDialog && (
                <AddHabitModal
                    isOpen={showEditDialog}
                    onClose={() => setShowEditDialog(false)}
                    editElement={element}
                    onUpdate={(updatedHabit) => {
                        updateElement(updatedHabit);
                        setShowEditDialog(false);
                    }}
                />
            )}
            <HabitHistoryModal
                isOpen={historyIsOpen}
                onClose={handleCloseHistory}
                element={element}
                onToggleComplete={handleToggleComplete}
                onSetProgress={(dateStr, progress) => {
                    updateElement({
                        ...element,
                        ...setHabitProgressDate(element, dateStr, progress),
                    });
                }}
                historyMonth={historyMonth}
                onHistoryMonthChange={onHistoryMonthChange}
            />
            {quickLogDate && (
                <div className="habit-quick-log-overlay" onClick={() => setQuickLogDate(undefined)}>
                    <div className="habit-quick-log-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>{t('habitElement.logProgress')}</h3>
                        <div className="habit-quick-log-field">
                            <label>{t('habitElement.howMuch')}</label>
                            <div className="habit-quick-log-input-row">
                                <input
                                    type="number"
                                    min="0"
                                    max={getHabitTargetCount(element)}
                                    step="1"
                                    value={quickLogValue}
                                    onChange={(e) => setQuickLogValue(e.target.value)}
                                    autoFocus
                                />
                                {element.unit && <span>{element.unit}</span>}
                            </div>
                        </div>
                        <div className="habit-quick-log-target">
                            {t('habitElement.target')}: {getTargetLabel()}
                        </div>
                        <div className="habit-quick-log-actions">
                            <button type="button" onClick={() => setQuickLogDate(undefined)}>
                                {t('habitElement.cancel')}
                            </button>
                            <button type="button" className="primary" onClick={handleSaveQuickLog}>
                                {t('habitElement.save')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
